import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE } from "../../config/app";
import "./Blog.css";

function cleanContent(value) {
  return String(value || "").split("\n").filter(Boolean);
}

function blogDate(value) {
  if (!value) return "Published recently";
  return new Date(value).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function RelatedBlog({ blog }) {
  return (
    <Link className="related-blog" to={`/blog/${blog.slug || blog.id}`}>
      <div className="related-blog-image">
        {blog.cover_image_url ? <img src={blog.cover_image_url} alt={`${blog.title} related article cover`} /> : <span>EC</span>}
      </div>
      <div>
        <p>{blog.category_name || blog.category || "Email marketing"}</p>
        <h3>{blog.title}</h3>
      </div>
    </Link>
  );
}

export default function BlogDetail({ initialData }) {
  const { slug } = useParams();
  const [blog, setBlog] = useState(() => initialData?.blog || null);
  const [related, setRelated] = useState(() => Array.isArray(initialData?.related) ? initialData.related : []);
  const [loading, setLoading] = useState(() => !initialData);
  const [error, setError] = useState("");
  const skipInitialFetch = useRef(Boolean(initialData?.blog));

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/public/blogs/${encodeURIComponent(slug)}`, { credentials: "include" })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.message || "Blog not found");
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setBlog(data.blog || null);
        setRelated(Array.isArray(data.related) ? data.related : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Blog not found");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const paragraphs = useMemo(() => cleanContent(blog?.content), [blog]);

  if (loading) {
    return <div className="blog-detail-shell"><div className="blog-detail-loading" /></div>;
  }

  if (error || !blog) {
    return (
      <div className="blog-detail-shell">
        <div className="blog-empty">
          <h1>Blog not found</h1>
          <p>{error || "This blog is not available."}</p>
          <Link to="/blog">Back to blogs</Link>
        </div>
      </div>
    );
  }

  return (
    <article className="blog-detail-shell">
      <div className="blog-detail-grid">
        <main className="blog-article">
          <Link className="blog-back-link" to="/blog">Back to blog</Link>
          <div className="blog-article-meta">
            <span>{blog.category_name || blog.category || "Email marketing"}</span>
            <span>{blogDate(blog.published_at)}</span>
          </div>
          <h1>{blog.title}</h1>
          {blog.excerpt && <p className="blog-article-excerpt">{blog.excerpt}</p>}
          {blog.cover_image_url && <img className="blog-article-cover" src={blog.cover_image_url} alt={`${blog.title} cover`} />}
          <div className="blog-content">
            {paragraphs.map((paragraph, index) => (
              paragraph.trim().startsWith("<") ? (
                <div key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
              ) : (
                <p key={index}>{paragraph}</p>
              )
            ))}
          </div>
        </main>

        <aside className="blog-sidebar">
          <div className="blog-sidebar-card">
            <h2>Related blogs</h2>
            <p>Picked by category first, then latest published blogs.</p>
            <div className="related-blog-list">
              {related.length ? related.map((item) => <RelatedBlog key={item.id} blog={item} />) : <span>No related blogs yet.</span>}
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
